import React, { useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, Camera, X } from 'lucide-react';
import { api } from '../lib/api';
import { US_STATES } from '../lib/shop';

const EMPTY = { name: '', email: '', phone: '', company: '', machine_model: '', serial_number: '', purchased_from_us: false, issue_type: 'repair', machine_down: false, description: '', city: '', state: '', preferred_contact: 'phone', photos: [] };
const inputCls = 'mt-1 w-full border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 focus:outline-none focus:border-brand-primary';
const L = ({ label, children, span = '' }) => (
    <label className={`block ${span}`}><span className="label-caps text-xs text-neutral-500">{label}</span>{children}</label>
);

const Photos = ({ photos, setPhotos }) => {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/service-requests/photo', fd);
            setPhotos([...photos, data.url]);
        } catch (er) { setErr(er.response?.data?.detail || 'Upload failed'); }
        setBusy(false);
    };
    return (
        <div className="sm:col-span-2">
            <span className="label-caps text-xs text-neutral-500">Photos (optional, up to 5)</span>
            <div className="mt-2 flex flex-wrap gap-3">
                {photos.map((p, i) => (
                    <div key={p} className="relative w-20 h-20 border border-neutral-200" data-testid={`service-photo-${i}`}>
                        <img src={p} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setPhotos(photos.filter((x) => x !== p))} className="absolute -top-2 -right-2 bg-white border border-neutral-300 rounded-full p-0.5" data-testid={`service-photo-remove-${i}`}><X className="h-3 w-3" /></button>
                    </div>
                ))}
                {photos.length < 5 && (
                    <label className="w-20 h-20 border border-dashed border-neutral-300 hover:border-brand-primary flex flex-col items-center justify-center text-neutral-500 cursor-pointer text-[10px] label-caps" data-testid="service-photo-add">
                        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Add
                        <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="service-photo-input" />
                    </label>
                )}
            </div>
            {err && <p className="mt-1 text-xs text-red-600" data-testid="service-photo-error">{err}</p>}
        </div>
    );
};

const ServiceRequestForm = () => {
    const [f, setF] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState('');
    const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError('');
        try {
            const { data } = await api.post('/service-requests', f);
            if (window.trackConversion) window.trackConversion('service_request', { issue: f.issue_type });
            setDone(data.request_number);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === 'string' ? d : 'Please fill in all required fields (description needs at least 10 characters).');
        }
        setBusy(false);
    };

    if (done) {
        return (
            <div className="border border-emerald-200 bg-emerald-50 p-8" data-testid="service-success">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                <h3 className="mt-4 font-display font-black uppercase text-3xl text-neutral-900">Request received</h3>
                <p className="mt-2 text-neutral-700">Request <b data-testid="service-request-number">{done}</b>. A technician will get back to you within 24 hours.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="service-form">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website || ''} onChange={upd('website')} className="hidden" aria-hidden="true" />
            <L label="What do you need?">
                <select value={f.issue_type} onChange={upd('issue_type')} className={inputCls} data-testid="service-issue-type">
                    <option value="repair">Repair</option><option value="maintenance">Maintenance / tune-up</option>
                    <option value="parts">Parts</option><option value="technical">Technical question</option>
                </select>
            </L>
            <L label="Machine make & model *"><input required value={f.machine_model} onChange={upd('machine_model')} placeholder="e.g. IC310S" className={inputCls} data-testid="service-machine-model" /></L>
            <L label="Serial number"><input value={f.serial_number} onChange={upd('serial_number')} className={inputCls} data-testid="service-serial" /></L>
            <div className="flex flex-col justify-end gap-2 pb-1">
                <label className="flex items-center gap-2 text-sm text-neutral-700"><input type="checkbox" checked={f.purchased_from_us} onChange={upd('purchased_from_us')} data-testid="service-purchased" /> Bought from Dry Ice Bulls</label>
                <label className="flex items-center gap-2 text-sm text-red-700 font-semibold"><input type="checkbox" checked={f.machine_down} onChange={upd('machine_down')} data-testid="service-machine-down" /> Machine is down — urgent</label>
            </div>
            <L label="Describe the problem *" span="sm:col-span-2"><textarea required minLength={10} rows={4} value={f.description} onChange={upd('description')} className={inputCls} data-testid="service-description" /></L>
            <Photos photos={f.photos} setPhotos={(photos) => setF({ ...f, photos })} />
            <L label="Full name *"><input required value={f.name} onChange={upd('name')} className={inputCls} data-testid="service-name" /></L>
            <L label="Company"><input value={f.company} onChange={upd('company')} className={inputCls} data-testid="service-company" /></L>
            <L label="Email *"><input required type="email" value={f.email} onChange={upd('email')} className={inputCls} data-testid="service-email" /></L>
            <L label="Phone *"><input required type="tel" value={f.phone} onChange={upd('phone')} className={inputCls} data-testid="service-phone" /></L>
            <div className="grid grid-cols-[1fr_96px] gap-3">
                <L label="City"><input value={f.city} onChange={upd('city')} className={inputCls} data-testid="service-city" /></L>
                <L label="State">
                    <select value={f.state} onChange={upd('state')} className={inputCls} data-testid="service-state">
                        <option value="">—</option>{US_STATES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                </L>
            </div>
            <L label="Best way to reach you">
                <select value={f.preferred_contact} onChange={upd('preferred_contact')} className={inputCls} data-testid="service-preferred-contact">
                    <option value="phone">Phone call</option><option value="text">Text</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option>
                </select>
            </L>
            {error && <p className="sm:col-span-2 text-sm text-red-600 flex gap-2" data-testid="service-error"><AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />{error}</p>}
            <button type="submit" disabled={busy} className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-bold px-6 py-4 label-caps transition-colors" data-testid="service-submit">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send service request
            </button>
        </form>
    );
};

export default ServiceRequestForm;
